import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  CheckSquare,
  Square,
  Loader2,
  ImageOff,
  Play,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { feedWatchService } from '../../../services/feedWatch';
import type { FeedProduct } from '../../../services/feedWatch';

// ── Field resolution (same aliases as social-posting ProductCard) ──

const FIELD_ALIASES: Record<string, string[]> = {
  image: ['image_link', 'image', 'main_image', 'g:image_link', 'thumbnail', 'image_url'],
  title: ['title', 'name', 'product_title', 'g:title', 'item_title'],
  price: ['price', 'sale_price', 'g:price', 'regular_price'],
  id: ['id', 'g:id', 'sku', 'item_id', 'product_id'],
};

function resolveField(raw: Record<string, unknown>, field: string): string {
  const aliases = FIELD_ALIASES[field];
  if (!aliases) return '';
  for (const alias of aliases) {
    const val = raw[alias];
    if (val !== null && val !== undefined && val !== '') return String(val);
  }
  return '';
}

// ── Selection state ───────────────────────────────────────────

type SelectionState =
  | { mode: 'all'; excluded: Set<number> }
  | { mode: 'custom'; included: Set<number> };

function defaultSelection(): SelectionState {
  return { mode: 'all', excluded: new Set() };
}

function isSelected(state: SelectionState, sortIndex: number): boolean {
  if (state.mode === 'all') return !state.excluded.has(sortIndex);
  return state.included.has(sortIndex);
}

function toggleOne(state: SelectionState, sortIndex: number): SelectionState {
  if (state.mode === 'all') {
    const excluded = new Set(state.excluded);
    if (excluded.has(sortIndex)) {
      excluded.delete(sortIndex);
    } else {
      excluded.add(sortIndex);
    }
    return { mode: 'all', excluded };
  }
  const included = new Set(state.included);
  if (included.has(sortIndex)) {
    included.delete(sortIndex);
  } else {
    included.add(sortIndex);
  }
  return { mode: 'custom', included };
}

function selectedCount(state: SelectionState, total: number): number {
  if (state.mode === 'all') return total - state.excluded.size;
  return state.included.size;
}

function toProductIndices(state: SelectionState, total: number): number[] | undefined {
  if (state.mode === 'all' && state.excluded.size === 0) return undefined;
  if (state.mode === 'all') {
    const indices: number[] = [];
    for (let i = 0; i < total; i++) {
      if (!state.excluded.has(i)) indices.push(i);
    }
    return indices;
  }
  return Array.from(state.included).sort((a, b) => a - b);
}

// ── Component ─────────────────────────────────────────────────

interface RenderProductModalProps {
  open: boolean;
  onClose: () => void;
  feedId: string;
  totalProducts: number;
  isRendering: boolean;
  onConfirm: (productIndices?: number[]) => void;
}

const PAGE_SIZE = 50;

export function RenderProductModal({
  open,
  onClose,
  feedId,
  totalProducts,
  isRendering,
  onConfirm,
}: RenderProductModalProps) {
  const [selection, setSelection] = useState<SelectionState>(defaultSelection);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [products, setProducts] = useState<FeedProduct[]>([]);
  const [page, setPage] = useState(1);
  const [serverTotal, setServerTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelection(defaultSelection());
      setSearch('');
      setDebouncedSearch('');
      setProducts([]);
      setPage(1);
      setHasMore(true);
      setServerTotal(0);
    }
  }, [open]);

  // Debounce search input — reset page on change (batched with React 18)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch products with server-side search
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const result = await feedWatchService.listProducts(
          feedId,
          page,
          PAGE_SIZE,
          debouncedSearch || undefined,
        );
        if (cancelled) return;
        // page === 1 means fresh search or initial load — replace products
        setProducts((prev) => (page === 1 ? result.data : [...prev, ...result.data]));
        setServerTotal(result.meta.total);
        setHasMore(page * PAGE_SIZE < result.meta.total);
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, feedId, page, debouncedSearch]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) setPage((p) => p + 1);
  }, [isLoading, hasMore]);

  const isSearching = debouncedSearch.trim().length > 0;
  const count = selectedCount(selection, totalProducts);
  const noneSelected = count === 0;
  const allSelected = count === totalProducts;

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelection({ mode: 'custom', included: new Set() });
    } else {
      setSelection(defaultSelection());
    }
  }, [allSelected]);

  const handleConfirm = useCallback(() => {
    const indices = toProductIndices(selection, totalProducts);
    onConfirm(indices);
  }, [selection, totalProducts, onConfirm]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isRendering) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">Render Products</DialogTitle>
          <DialogDescription className="text-sm">
            Select which products to render. Each product generates one image using your design.
          </DialogDescription>
        </DialogHeader>

        {/* Search + Select All */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or ID..."
              className="h-10 pl-10 text-sm rounded-xl"
            />
          </div>
          {!isSearching && (
            <Button
              variant="outline"
              className="rounded-lg h-10 px-4 shrink-0 text-sm"
              onClick={toggleAll}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
          <div className="space-y-1.5 py-1">
            {products.map((product) => {
              const checked = isSelected(selection, product.sort_index);
              const title = resolveField(product.raw_data, 'title') || `Product #${product.sort_index}`;
              const image = resolveField(product.raw_data, 'image');
              const productId = resolveField(product.raw_data, 'id');
              const price = resolveField(product.raw_data, 'price');

              return (
                <button
                  key={product.id}
                  onClick={() => setSelection((s) => toggleOne(s, product.sort_index))}
                  className={`
                    w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-colors
                    ${checked ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50 border border-transparent'}
                  `}
                >
                  {checked ? (
                    <CheckSquare className="h-5 w-5 text-primary shrink-0" />
                  ) : (
                    <Square className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}

                  <div className="h-12 w-12 rounded-xl border overflow-hidden bg-muted/30 shrink-0">
                    {image ? (
                      <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <ImageOff className="h-5 w-5 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium truncate">{title}</p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      {productId && <span className="font-mono truncate">{productId}</span>}
                      {price && <span className="tabular-nums">{price}</span>}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Load more */}
            {!isLoading && hasMore && products.length > 0 && (
              <div className="flex justify-center py-3">
                <Button variant="outline" size="sm" className="rounded-lg" onClick={loadMore}>
                  Load More ({products.length} of {serverTotal})
                </Button>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && products.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm">
                {isSearching ? `No products match "${debouncedSearch}".` : 'No products found.'}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t">
          <p className="text-sm text-muted-foreground">
            {count.toLocaleString()} of {totalProducts.toLocaleString()} selected
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-lg h-10 px-5 text-sm"
              onClick={onClose}
              disabled={isRendering}
            >
              Cancel
            </Button>
            <Button
              className="rounded-lg h-10 px-5 text-sm"
              onClick={handleConfirm}
              disabled={noneSelected || isRendering}
            >
              {isRendering ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1.5" />
              )}
              {isRendering
                ? 'Starting...'
                : `Render ${count.toLocaleString()} Product${count !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
